import React, { createContext, useContext, useReducer, useEffect } from "react";
import authService from "../services/authService";

// Authentication context
const AuthContext = createContext();

// Authentication state actions
const AUTH_ACTIONS = {
  SET_LOADING: "SET_LOADING",
  SET_USER: "SET_USER",
  SET_ERROR: "SET_ERROR",
  CLEAR_ERROR: "CLEAR_ERROR",
  SIGN_OUT: "SIGN_OUT",
};

// Initial authentication state
const initialState = {
  user: null,
  loading: true,
  error: null,
  isAuthenticated: false,
};

// Authentication state reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };
    case AUTH_ACTIONS.SET_USER:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        loading: false,
        error: null,
      };
    case AUTH_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };
    case AUTH_ACTIONS.SIGN_OUT:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        loading: false,
        error: null,
      };
    default:
      return state;
  }
};

/**
 * Custom hook to access authentication context
 * @returns {Object} Authentication context value
 * @throws {Error} If used outside of AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

/**
 * Authentication provider component
 * Manages authentication state and provides auth operations
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Set up authentication state listener on mount
  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged((user) => {
      dispatch({ type: AUTH_ACTIONS.SET_USER, payload: user });
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  /**
   * Sign in an admin user
   * @param {string} email - Admin email address
   * @param {string} password - Admin password
   * @returns {Promise<Object>} User object
   * @throws {Error} If authentication fails
   */
  const signIn = async (email, password) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      const user = await authService.signInAdmin(email, password);

      // User state will be updated by the onAuthStateChanged listener
      return user;
    } catch (error) {
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    }
  };

  /**
   * Sign out the current user
   * @returns {Promise<void>}
   * @throws {Error} If sign out fails
   */
  const signOut = async () => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      await authService.signOutAdmin();

      // Manually dispatch sign out to ensure immediate state update
      dispatch({ type: AUTH_ACTIONS.SIGN_OUT });
    } catch (error) {
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    }
  };

  /**
   * Change the current user's password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise<void>}
   * @throws {Error} If password change fails
   */
  const changePassword = async (currentPassword, newPassword) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      await authService.changePassword(currentPassword, newPassword);

      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    } catch (error) {
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: error.message });
      throw error;
    }
  };

  /**
   * Clear any authentication errors
   */
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  /**
   * Check if the current user is an admin
   * @returns {Promise<boolean>} True if user is admin
   */
  const isAdmin = async () => {
    return await authService.isAdmin();
  };

  /**
   * Get the current authenticated user
   * @returns {Object|null} Current user or null
   */
  const getCurrentUser = () => {
    return authService.getCurrentUser();
  };

  // Context value with state and operations
  const value = {
    // State
    user: state.user,
    loading: state.loading,
    error: state.error,
    isAuthenticated: state.isAuthenticated,

    // Operations
    signIn,
    signOut,
    logout: signOut, // Alias for backward compatibility
    changePassword,
    clearError,
    isAdmin,
    getCurrentUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
